import React, { useEffect, useState } from 'react';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import { db } from '../services/firebase';
import type { Language, Subscription, User } from '../types';
import { getTranslation } from '../utils/translations';

interface ApproveSubscriptionModalProps {
    user: User | null;
    language?: Language;
    onClose: () => void;
    onApproved?: () => void;
}

const defaultExpiryDate = (): string => {
    const d = new Date();
    d.setMonth(d.getMonth() + 1);
    return d.toISOString().slice(0, 10);
};

const fromDateInputValue = (value: string): string =>
    new Date(`${value}T23:59:59.999`).toISOString();

const ApproveSubscriptionModal: React.FC<ApproveSubscriptionModalProps> = ({
    user,
    language = 'en',
    onClose,
    onApproved,
}) => {
    const t = (key: Parameters<typeof getTranslation>[1]) => getTranslation(language, key);
    const [freeForLife, setFreeForLife] = useState(false);
    const [endDate, setEndDate] = useState(defaultExpiryDate);
    const [isSaving, setIsSaving] = useState(false);
    const [error, setError] = useState('');

    useEffect(() => {
        if (!user) return;
        setFreeForLife(false);
        setEndDate(defaultExpiryDate());
        setError('');
    }, [user]);

    if (!user) return null;

    const displayName =
        user.nickname || `${user.firstName || ''} ${user.lastName || ''}`.trim() || user.email;

    const handleApprove = async () => {
        if (!freeForLife && !endDate) {
            setError(t('subscriptionEndDateRequired'));
            return;
        }
        setIsSaving(true);
        setError('');
        try {
            const userRef = doc(db, 'users', user.uid);
            const userSnap = await getDoc(userRef);
            if (!userSnap.exists()) throw new Error('User not found');

            const userData = userSnap.data() as User;
            const base = userData.subscription ?? {
                startDate: new Date().toISOString(),
                planDetails: { price: 0, currency: 'USD', households: 5 },
                adminViewed: false,
                status: 'pending' as const,
                endDate: null,
                plan: 'free',
            };

            const subscription: Subscription = {
                ...base,
                status: 'subscribed',
                adminViewed: true,
                startDate: base.startDate || new Date().toISOString(),
                plan: freeForLife ? 'free_for_life' : 'free',
                endDate: freeForLife ? null : fromDateInputValue(endDate),
            };

            await updateDoc(userRef, { subscription });
            onApproved?.();
            onClose();
        } catch (err) {
            console.error('Failed to approve subscription', err);
            setError(t('errorSaving'));
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[120] p-4">
            <div
                className="bg-white dark:bg-slate-900 w-full max-w-md rounded-2xl shadow-2xl border dark:border-slate-800 overflow-hidden"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="p-6 border-b dark:border-slate-800 bg-green-50 dark:bg-green-900/20">
                    <h3 className="text-lg font-black text-slate-800 dark:text-slate-100">{t('approveSubscription')}</h3>
                    <p className="text-sm text-slate-600 dark:text-slate-400 mt-1 truncate">{displayName}</p>
                    <p className="text-xs text-slate-400 dark:text-slate-500 dir-ltr text-start mt-0.5">{user.email}</p>
                </div>

                <div className="p-6 space-y-4">
                    {error && (
                        <div className="bg-red-50 dark:bg-red-900/30 text-red-700 dark:text-red-300 p-3 rounded-xl text-sm font-bold">
                            {error}
                        </div>
                    )}

                    <p className="text-sm text-slate-600 dark:text-slate-400">{t('approveSubscriptionHint')}</p>

                    <label className="flex items-center gap-3 cursor-pointer p-3 rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/50">
                        <input
                            type="checkbox"
                            checked={freeForLife}
                            onChange={(e) => setFreeForLife(e.target.checked)}
                            className="rounded border-slate-300 text-brand-sage focus:ring-brand-sage"
                        />
                        <span className="text-sm font-bold text-slate-700 dark:text-slate-300">{t('plan_free_for_life')}</span>
                    </label>

                    {!freeForLife && (
                        <div className="space-y-1">
                            <label className="text-[10px] font-black text-slate-400 dark:text-slate-500 uppercase tracking-widest">
                                {t('subscriptionEnds')}
                            </label>
                            <input
                                type="date"
                                value={endDate}
                                onChange={(e) => setEndDate(e.target.value)}
                                className="w-full p-3 border border-slate-200 dark:border-slate-700 rounded-xl bg-slate-50 dark:bg-slate-800 text-sm font-bold text-slate-700 dark:text-slate-300 outline-none focus:ring-2 focus:ring-brand-sage"
                            />
                        </div>
                    )}
                </div>

                <div className="p-4 bg-slate-50 dark:bg-slate-800 border-t dark:border-slate-700 flex gap-2">
                    <button
                        type="button"
                        onClick={onClose}
                        disabled={isSaving}
                        className="flex-1 py-3 bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-300 font-black rounded-xl text-xs uppercase tracking-widest hover:bg-slate-200 dark:hover:bg-slate-600 transition-colors disabled:opacity-50"
                    >
                        {t('cancel')}
                    </button>
                    <button
                        type="button"
                        onClick={() => void handleApprove()}
                        disabled={isSaving}
                        className="flex-1 py-3 bg-green-600 text-white font-black rounded-xl text-xs uppercase tracking-widest hover:bg-green-700 transition-colors disabled:opacity-50"
                    >
                        {isSaving ? '...' : t('approveSubscription')}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default ApproveSubscriptionModal;
